/**
 * La navigation du parcours installateur et poseur, en deux formes selon la largeur.
 *
 * Pourquoi deux formes. Les six entrées tenaient dans l'en-tête, à droite du logo, avec
 * `flex-wrap`. Sur un écran de bureau c'est propre ; en 390 px elles s'enroulaient sur
 * trois lignes, le badge de rôle passait SOUS l'icône « Nouveau chantier » (les deux
 * mots se chevauchaient, illisibles), et chaque entrée faisait 18 px de haut, contre
 * 44 px recommandés pour une cible tactile. Or le poseur travaille debout, sur tablette,
 * chez un client qui attend : c'est son seul contexte d'usage.
 *
 * En dessous de `sm` (640 px), les vues passent donc dans une barre fixe en bas d'écran,
 * atteignable au pouce, et l'en-tête ne garde que l'identité et la langue. Au-dessus, la
 * barre d'en-tête d'origine est conservée telle quelle : elle n'a jamais posé problème,
 * et la changer serait un risque gratuit.
 *
 * ⚠️ Les `data-testid` canoniques (`nav-home-btn`, `nav-equipe-btn`, …) restent portés
 * par la SEULE forme bureau. `deploy/recette-navigateur.js` les cible et Playwright
 * refuse un sélecteur qui désigne deux nœuds : les dupliquer casserait la recette de la
 * chaîne d'intégration. Les onglets du bas portent donc les mêmes noms suffixés
 * `-mobile`.
 *
 * ⚠️ Le sélecteur de langue reste dans l'en-tête et ne part PAS dans « Plus ». Les
 * poseurs ne sont pas tous francophones et signent chez des clients : changer de langue
 * doit rester à un seul geste.
 */
import { useState } from 'react';
import { Home, FileSignature, History, Users2, User2, LogOut, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import LanguageSwitcher from '@/shared/components/LanguageSwitcher';

/** Une entrée de la barre du bas. 56 px de haut, soit au-delà des 44 px recommandés. */
function Onglet({ icone: Icone, libelle, actif, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      aria-current={actif ? 'page' : undefined}
      className={`flex-1 min-h-[56px] flex flex-col items-center justify-center gap-1 px-1
                  transition-colors ${actif ? 'text-white' : 'text-white/55'}`}
      data-testid={testid}
    >
      <Icone className="w-5 h-5" />
      <span className="text-[10px] font-medium leading-none truncate max-w-full">{libelle}</span>
    </button>
  );
}

/** Une ligne de la feuille « Plus ». Même exigence de hauteur que les onglets. */
function LignePlus({ icone: Icone, libelle, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      className={`w-full min-h-[52px] flex items-center gap-3 px-4 rounded-lg text-[14px]
                  hover:bg-neutral-100 ${danger ? 'text-red-600' : 'text-marque'}`}
      data-testid={testid}
    >
      <Icone className="w-4.5 h-4.5" />
      {libelle}
    </button>
  );
}

export default function NavigationInstallateur({
  view, setView, isPoseur, user, t, langue, changerLangue, onProfil, onLogout,
}) {
  const [plusOuvert, setPlusOuvert] = useState(false);
  const allerA = (vue) => { setPlusOuvert(false); setView(vue); };

  // Une entree par vue, comme chez lui : le bouton a bascule d'avant ne disait jamais ou
  // on se trouvait, et devenait faux des qu'il y a plus de deux vues.
  const VUES = [
    { vue: 'home', icone: Home, libelle: t('nav_home'), court: t('nav_home'), testid: 'nav-home-btn' },
    { vue: 'wizard', icone: FileSignature, libelle: t('nav_new_chantier'), court: t('nav_new_short', 'Nouveau'), testid: 'nav-wizard-btn' },
    { vue: 'mes-chantiers', icone: History, libelle: t('nav_my_chantiers'), court: t('nav_chantiers_short', 'Chantiers'), testid: 'installer-chantiers-btn' },
  ];

  return (
    <>
      {/* En-tete, sur toutes les largeurs */}
      <div className="bg-marque px-4 py-3 flex items-center justify-between gap-3 sticky top-0 z-10">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/cftsign-192.png" alt="CFTSign" className="w-7 h-7 rounded-md shrink-0" />
          {/* CFTSign est la marque du parcours de signature, celle que le poseur et
              l'installateur ont sous les yeux sur le terrain. Le fichier de logo
              portait deja ce nom, seul le libelle disait encore FlexCore. */}
          <span className="text-[15px] font-semibold text-white">CFTSign</span>
          <span className="text-[10px] text-white/50 px-1.5 py-0.5 bg-white/10 rounded whitespace-nowrap">
            {isPoseur ? t('role_poseur', 'Poseur') : t('role_installateur', 'Installateur')}
          </span>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Le selecteur ne s'affiche QUE sur ce parcours : c'est le seul traduit,
              et le montrer ailleurs promettrait une traduction qui n'existe pas. */}
          <LanguageSwitcher lang={langue} onChange={changerLangue} dark />

          {/* Forme BUREAU : au-dessus de 640 px seulement. Porte les testid canoniques. */}
          <div className="hidden sm:flex items-center gap-3 flex-wrap justify-end">
            {VUES.map(({ vue, icone: Icone, libelle, testid }) => (
              <button
                key={vue}
                onClick={() => setView(vue)}
                aria-current={view === vue ? 'page' : undefined}
                className={`text-[12px] flex items-center gap-1 ${view === vue ? 'text-white' : 'text-white/60 hover:text-white'}`}
                data-testid={testid}
              >
                <Icone className="w-3.5 h-3.5" /> {libelle}
              </button>
            ))}
            {!isPoseur && (
              <button
                onClick={() => setView('equipe')}
                aria-current={view === 'equipe' ? 'page' : undefined}
                className={`text-[12px] flex items-center gap-1 ${view === 'equipe' ? 'text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="nav-equipe-btn"
              >
                <Users2 className="w-3.5 h-3.5" /> {t('nav_team')}
              </button>
            )}
            <button
              onClick={onProfil}
              className="text-[12px] text-white/60 hover:text-white flex items-center gap-1"
              data-testid="installer-profile-btn"
            >
              <User2 className="w-3.5 h-3.5" /> {t('nav_profile', 'Mon profil')}
            </button>
            <button
              onClick={onLogout}
              className="text-[12px] text-white/50 hover:text-white flex items-center gap-1"
              data-testid="installer-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> {t('nav_logout', 'Déconnexion')}
            </button>
          </div>
        </div>
      </div>

      {/* Forme MOBILE : barre fixe en bas, en dessous de 640 px.
          `pb-[env(safe-area-inset-bottom)]` tient compte de la barre gestuelle des
          iPhone a encoche, sans quoi le dernier onglet passe dessous. */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-marque border-t border-white/10
                   flex items-stretch pb-[env(safe-area-inset-bottom)]"
        data-testid="nav-mobile"
        aria-label={t('nav_home')}
      >
        {VUES.map(({ vue, icone, court, testid }) => (
          <Onglet
            key={vue}
            icone={icone}
            libelle={court}
            actif={view === vue}
            onClick={() => allerA(vue)}
            testid={`${testid}-mobile`}
          />
        ))}
        <Onglet
          icone={Menu}
          libelle={t('nav_more', 'Plus')}
          actif={plusOuvert || view === 'equipe'}
          onClick={() => setPlusOuvert(true)}
          testid="nav-plus-btn-mobile"
        />
      </nav>

      {/* La feuille « Plus » : ce qui ne tient pas dans quatre onglets. */}
      <Sheet open={plusOuvert} onOpenChange={setPlusOuvert}>
        <SheetContent side="bottom" className="sm:hidden rounded-t-2xl" data-testid="nav-plus-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-[15px]">{user?.name || t('nav_more', 'Plus')}</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-1 pb-[env(safe-area-inset-bottom)]">
            {!isPoseur && (
              <LignePlus
                icone={Users2}
                libelle={t('nav_team')}
                onClick={() => allerA('equipe')}
                testid="nav-equipe-btn-mobile"
              />
            )}
            <LignePlus
              icone={User2}
              libelle={t('nav_profile', 'Mon profil')}
              onClick={() => { setPlusOuvert(false); onProfil(); }}
              testid="installer-profile-btn-mobile"
            />
            <LignePlus
              icone={LogOut}
              libelle={t('nav_logout', 'Déconnexion')}
              onClick={onLogout}
              testid="installer-logout-btn-mobile"
              danger
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
